import { FORM_DESCRIPTION_MAX_CHARS, STATUS_ACTIVE } from "src/constants";
import * as GQL from "src/types/graphql";
import { z } from "zod";

/** Label for audience school pickers; non-active statuses are shown explicitly. */
export function audienceSchoolDropdownLabel(
  name: string,
  status: string
): string {
  if (status === STATUS_ACTIVE) {
    return name;
  }
  return `${name} (Status: ${status})`;
}

// Add new options on the "What should happen after this form?" dropdown here.
export enum WhatShouldHappenEnum {
  Submit = "submit",
  SubmitAndContinue = "submitAndContinue",
}

const WhatShouldHappenSchema = z
  .union([
    z.literal(WhatShouldHappenEnum.Submit),
    z.literal(WhatShouldHappenEnum.SubmitAndContinue),
  ])
  .optional();

const StatusOrSubstatusSchema = z
  .union([
    z.literal(GQL.form_status_enum.InProgress),
    z.literal(GQL.form_status_enum.Submitted),
    z.literal(GQL.form_status_enum.Verified),
    z.literal(GQL.form_status_enum.LotteryReady),
    z.literal(GQL.form_status_enum.Admissions),
    z.literal(GQL.waitlist_status_enum.Waitlisted),
    z.literal(GQL.waitlist_status_enum.Withdrawn),
    z.literal(GQL.offer_status_enum.Offered),
    z.literal(GQL.offer_status_enum.Accepted),
    z.literal(GQL.offer_status_enum.Declined),
    // Just to type checking
    z.literal(GQL.form_status_enum.Deleted),
    z.literal(GQL.form_status_enum.Cancelled),
    z.literal(GQL.form_status_enum.Withdrawn),
    z.literal(GQL.waitlist_status_enum.Removed),
    z.literal(GQL.offer_status_enum.Revoked),
  ])
  .nullish()
  .optional();

export const FormTemplateStatusRuleSchema = z
  .object({
    id: z.string().optional(),
    type: z.literal(GQL.rule_type_enum.StatusRule).optional(),
    whatShouldHappen: WhatShouldHappenSchema,
    nextTemplateId: z.string().optional(),
    statusOrSubstatus: StatusOrSubstatusSchema,
  })
  .nullish();

export const AudienceTypeEnum = z.enum(["everyone", "new", "current"]);
export type AudienceType = z.infer<typeof AudienceTypeEnum>;

export const AllowDeclineAcceptedOfferEnum = z.enum([
  "none",
  "family",
  "admin",
  "both",
]);
export type AllowDeclineAcceptedOffer = z.infer<
  typeof AllowDeclineAcceptedOfferEnum
>;

export const StaffOverrideReasonTypeEnum = z.enum(["freeText", "options"]);
export type StaffOverrideReasonType = z.infer<
  typeof StaffOverrideReasonTypeEnum
>;

export const FormTemplateSettingsSchema = z
  .object({
    name: z.string().min(1, "Form name is required."),
    key: z.string().min(1, "Form key is required."),
    userGeneratedKey: z.boolean(),
    description: z
      .string()
      .max(
        FORM_DESCRIPTION_MAX_CHARS,
        `Description must be ${FORM_DESCRIPTION_MAX_CHARS} characters or less.`
      ),
    openAt: z.string().optional(),
    closedAt: z.string().optional(),
    offerStartAt: z.string().optional(),
    reopenAt: z.string().optional(),
    lotteryAndOffersEnabled: z.boolean(),
    allowDeclineAcceptedOfferEnabled: z.boolean().default(false),
    allowDeclineAcceptedOffer: AllowDeclineAcceptedOfferEnum.default("none"),
    active: z.boolean(),
    locked: z.boolean(),
    allowFormDeletionByFamily: z.boolean().default(false),
    schoolSpecificMessagesEnabled: z.boolean().default(false),
    showWaitlistPosition: z.boolean().default(false),
    audienceType: AudienceTypeEnum.default("everyone"),
    audienceSchoolIds: z.array(z.string()).default([]),
    allowStaffSchoolSelectionOverride: z.boolean().default(false),
    staffOverrideEligibleLabel: z.string().default(""),
    staffOverrideIneligibleLabel: z.string().default(""),
    staffOverrideReasonType: StaffOverrideReasonTypeEnum.default("freeText"),
    staffOverrideReasonRequired: z.boolean().default(true),
    staffOverrideReasonOptions: z.array(z.string()).default([]),
  })
  .superRefine((value, ctx) => {
    if (
      value.allowDeclineAcceptedOfferEnabled &&
      value.allowDeclineAcceptedOffer === "none"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["allowDeclineAcceptedOffer"],
        message:
          "Please select who can decline Accepted offers (Family, Staff, or Both).",
      });
    }
    if (
      value.allowStaffSchoolSelectionOverride &&
      value.staffOverrideReasonType === "options" &&
      value.staffOverrideReasonOptions.filter((o) => o.trim() !== "").length ===
        0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["staffOverrideReasonOptions"],
        message: "Add at least one override reason option.",
      });
    }
  });

export type FormTemplateSettingsType = z.infer<
  typeof FormTemplateSettingsSchema
>;
